import { Link, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { useOrgRole } from "@/hooks/useOrganization";
import { Button } from "@/components/ui/button";
import { Shield, LayoutDashboard, ClipboardCheck, Footprints, Award, FileText, ScrollText, BookOpen, Settings, LogOut, Building2, Users, ListTodo, HelpCircle, CheckSquare, KeyRound, ShieldAlert, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import InviteBanner from "@/components/InviteBanner";
import OnboardingTour, { useOnboardingTour } from "@/components/onboarding/OnboardingTour";
import FeedbackButton from "@/components/FeedbackButton";
import { useIdleTimeout } from "@/hooks/useIdleTimeout";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/diy", label: "Get Credentialed", icon: Footprints },
  { to: "/my-credentials", label: "Credentials & Renewal Hub", icon: Award },
  { to: "/oversight", label: "Oversight", icon: ClipboardCheck, practiceType: "employed" },
  { to: "/my-tasks", label: "My Tasks", icon: CheckSquare, practiceType: "_org" },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/vault", label: "Credential Vault", icon: KeyRound },
  { to: "/log", label: "Activity Log", icon: ScrollText },
  { to: "/resources", label: "Resources", icon: BookOpen },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/help", label: "Help", icon: HelpCircle },
];

const orgNavItems = [
  { to: "/org/dashboard", label: "Org Dashboard", icon: Building2 },
  { to: "/org/members", label: "Members", icon: Users },
  { to: "/org/assignments", label: "Task Dispatch", icon: ListTodo },
  { to: "/org/access-review", label: "Access Review", icon: ShieldCheck },
  { to: "/audit", label: "Audit Log", icon: ShieldAlert },
];

export default function AppLayout() {
  const { signOut } = useAuth();
  const { data: profile } = useProfile();
  const { isAdmin, orgId } = useOrgRole();
  const location = useLocation();
  const { showTour, startTour, completeTour } = useOnboardingTour();
  useIdleTimeout();

  const filteredNav = navItems.filter((item) => {
    if (item.practiceType === "_org") return !!profile?.organization_id;
    if (!item.practiceType) return true;
    if (!profile?.practice_type) return true;
    return item.practiceType === profile.practice_type;
  });

  const showOrgNav = !!orgId && isAdmin;
  const mobileNavItems = [
    ...filteredNav.slice(0, 4).map((item) => ({
      ...item,
      isActive: location.pathname === item.to,
    })),
    ...(showOrgNav
      ? [
          {
            to: "/org/dashboard",
            label: "Org",
            icon: Building2,
            isActive: location.pathname.startsWith("/org"),
          },
        ]
      : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      <InviteBanner />

      <header className="sticky top-0 z-40 border-b bg-card">
        <div className="container">
          <div className="flex h-12 items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <div className="flex flex-col leading-none">
                <span className="font-display text-lg font-bold"><span className="text-primary">Credential</span><span className="text-foreground">Edge</span></span>
                <span className="text-[9px] tracking-wide text-muted-foreground">Credentialing Made Simple</span>
              </div>
              <Badge variant="outline" className="ml-1 h-4 border-primary/40 px-1.5 py-0 text-[10px] font-semibold text-primary">BETA</Badge>
            </Link>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={startTour} className="hidden h-8 w-8 md:flex" title="Take a tour">
                <HelpCircle className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={signOut} className="hidden gap-1.5 md:flex">
                <LogOut className="h-4 w-4" /> Sign out
              </Button>
            </div>
          </div>
          <nav className="hidden items-stretch gap-1 border-t border-border/50 pb-2 pt-2 md:flex">
            {filteredNav.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "flex flex-1 flex-col items-center justify-center gap-0.5 rounded-md px-1 py-2 text-center transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "bg-secondary/10 text-secondary hover:bg-secondary/20"
                  )}
                >
                  <Icon className={cn("h-4 w-4", !isActive && "drop-shadow-[0_0_4px_hsl(var(--secondary))]")} />
                  <span className="text-xs font-medium leading-tight">{item.label}</span>
                </Link>
              );
            })}
            {showOrgNav && (
              <>
                <div className="mx-1 w-px self-stretch bg-border" />
                {orgNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.to;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={cn(
                        "flex flex-1 flex-col items-center justify-center gap-0.5 rounded-md px-1 py-2 text-center transition-colors",
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "bg-secondary/10 text-secondary hover:bg-secondary/20"
                      )}
                    >
                      <Icon className={cn("h-4 w-4", !isActive && "drop-shadow-[0_0_4px_hsl(var(--secondary))]")} />
                      <span className="text-xs font-medium leading-tight">{item.label}</span>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>
        </div>
      </header>

      <main className="container py-6">
        <Outlet />
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-40 border-t bg-card px-2 py-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] md:hidden">
        <div
          className="grid items-stretch gap-1"
          style={{ gridTemplateColumns: `repeat(${mobileNavItems.length}, minmax(0, 1fr))` }}
        >
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                aria-label={item.label}
                className={cn(
                  "relative flex min-h-12 min-w-12 w-full flex-col items-center justify-center gap-1 rounded-md px-1 py-2.5 text-center text-[11px] leading-tight",
                  item.isActive ? "bg-primary/10 text-primary" : "text-muted-foreground"
                )}
              >
                {item.isActive && <span className="absolute left-1/2 top-0 h-0.5 w-8 -translate-x-1/2 rounded-full bg-primary" />}
                <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span className="block max-w-full break-words">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      <FeedbackButton />
      {showTour && <OnboardingTour onComplete={completeTour} />}
    </div>
  );
}
